import React, { useEffect } from "react";
import { Avatar, Drawer, DrawerContent, Menu, MenuItem, type DrawerSelectEvent, type MenuSelectEvent } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { useNavigate, useLocation } from "react-router-dom";
import { userIcon, logoutIcon, menuIcon } from "@progress/kendo-svg-icons";
import { useShallow } from "zustand/react/shallow";
import type { SidebarProps as SidebarProperties } from "./SidebarProps";
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { AuthService } from "@infrastructure/services";
import { useInjection } from "@presentation/components/dependencyInjection";
import { logOutInProgress } from "@presentation/components/helpers/constants";
import { ProfileWindow } from "./ProfileWindow";
import { WorkspaceFilters } from "@presentation/components/layout/WorkspaceFilters";
import "./styles.css";

const SELECTED_ROUTE_STORAGE_KEY = "selectedSidebarRoute";
const findDefaultRouteIndex = (routes: SidebarProperties["routes"]) => {
  const homeIndex = routes.findIndex((route) => route.text === "Home" && route.route);
  return homeIndex === -1
    ? Math.max(
        routes.findIndex((route) => route.route),
        0,
      )
    : homeIndex;
};
const findRouteIndexByPath = (routes: SidebarProperties["routes"], path: string | null | undefined) => (path ? routes.findIndex((route) => route.route === path) : -1);
const resolveSidebarRouteIndex = (routes: SidebarProperties["routes"], path: string): number => {
  const exactIndex = findRouteIndexByPath(routes, path);
  if (exactIndex >= 0) return exactIndex;
  if (/^\/teams\/[^/]+(?:\/projects\/[^/]+)?\/configuration$/.test(path)) return findRouteIndexByPath(routes, "/settings");
  if (/^\/teams\/[^/]+\/projects(\/|$)/.test(path)) return findRouteIndexByPath(routes, "/projects");
  if (path.startsWith("/teams")) return findRouteIndexByPath(routes, "/teams");
  return path.startsWith("/projects") ? findRouteIndexByPath(routes, "/projects") : -1;
};

const Sidebar: React.FC<SidebarProperties> = (properties) => {
  const drawerRoutes = React.useMemo(() => properties.routes.filter((route) => route.route), [properties.routes]);
  const { setSessionState, username } = useUserManagementStore(useShallow((state) => ({ setSessionState: state.setSessionState, username: state.userSession?.username ?? "Undefined" })));
  const navigate = useNavigate();
  const location = useLocation();
  const authService = useInjection(AuthService);
  const [selectedIndex, setSelectedIndex] = React.useState(() => {
    const index = resolveSidebarRouteIndex(drawerRoutes, location.pathname);
    if (index >= 0) return index;
    const savedIndex = findRouteIndexByPath(drawerRoutes, localStorage.getItem(SELECTED_ROUTE_STORAGE_KEY));
    return savedIndex >= 0 ? savedIndex : findDefaultRouteIndex(drawerRoutes);
  });
  const [isExpanded, setIsExpanded] = React.useState(localStorage.getItem("sidebarisExpanded") === "true");
  const [showProfileWindow, setShowProfileWindow] = React.useState(false);

  useEffect(() => {
    const selectedRoute = drawerRoutes[selectedIndex]?.route;
    if (selectedRoute) localStorage.setItem(SELECTED_ROUTE_STORAGE_KEY, selectedRoute);
  }, [drawerRoutes, selectedIndex]);
  useEffect(() => {
    localStorage.setItem("sidebarisExpanded", String(isExpanded));
  }, [isExpanded]);
  useEffect(() => {
    const index = resolveSidebarRouteIndex(drawerRoutes, location.pathname);
    setSelectedIndex(index >= 0 ? index : findDefaultRouteIndex(drawerRoutes));
  }, [drawerRoutes, location.pathname]);

  const onSelect = (event: DrawerSelectEvent) => {
    const route = drawerRoutes[event.itemIndex]?.route;
    if (!route || (route === "/settings" && location.pathname === "/settings")) return;
    navigate(route);
    setSelectedIndex(event.itemIndex);
  };
  const onMenuItemSelect = (event: MenuSelectEvent) => {
    if (event.item.text === "Logout") {
      sessionStorage.setItem(logOutInProgress, "true");
      setSessionState(null, null, null);
      try {
        authService.signOut();
      } catch (error) {
        console.warn("Sign out failed:", error);
      }
      navigate("/", { replace: true });
    } else if (event.item.text === "My Profile") setShowProfileWindow(true);
  };
  const renderMenuItem = () => (
    <span className="username">
      <Avatar rounded="full" type="icon" className="user-avatar">
        <SvgIcon icon={userIcon} />
      </Avatar>
      {username}
    </span>
  );

  return (
    <div className="main-container">
      <div className="header-toolbar">
        <div className="flex-center">
          <Button className="header-button" themeColor="primary" svgIcon={menuIcon} onClick={() => setIsExpanded((previous) => !previous)} />
          <span className="flex-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{ display: "flex", alignItems: "center", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
              title="Go to home"
              aria-label="Go to home"
            >
              <img src="/tab_omicsFlowLogo_RGB2_16x16.svg" className="header-logo" alt="OmicsFlow logo" />
              <div className="header-title">OmicsFlow</div>
            </button>
            <WorkspaceFilters />
          </span>
        </div>
        <div className="flex-center">
          <Menu onSelect={onMenuItemSelect}>
            <MenuItem render={renderMenuItem}>
              <MenuItem svgIcon={userIcon} text="My Profile" />
              <MenuItem svgIcon={logoutIcon} text="Logout" />
            </MenuItem>
          </Menu>
          <div className="push-left">&nbsp;</div>
        </div>
      </div>
      <Drawer
        className="main-container"
        expanded={isExpanded}
        position="start"
        mode="push"
        mini={true}
        items={drawerRoutes.map((item, index) => {
          const drawerItem = { ...item, selected: index === selectedIndex };
          delete (drawerItem as { requiredPermission?: unknown }).requiredPermission;
          return drawerItem;
        })}
        onSelect={onSelect}
      >
        <DrawerContent className="drawer-content">
          <div style={{ minWidth: 0, height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>{properties.children}</div>
        </DrawerContent>
      </Drawer>
      {showProfileWindow && <ProfileWindow username={username} onClose={() => setShowProfileWindow(false)} />}
    </div>
  );
};

export { Sidebar };
